import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { injectIntl } from 'react-intl';

import { SERVICES_CODES, SERVICES_PROPERTIES } from 'app/config/services';
import { setupLicenseInicialValues, setupLicensePayload } from 'app/utils/forms/services/licenses';
import {
  validateLicenseDuplicateRequest,
  validateLicenseRequest,
} from 'app/utils/forms/validators/license';
import { CircularProgress } from '@material-ui/core';
import { FormServiceContext } from 'app/utils/contexts';

const Licenses = props => {
  const {
    intl,
    classes,
    loading,
    loadingDocuments,
    setLoadingDocuments,
    onCancel,
    onSave,
    onSubmit,
    initialValues,
    setInitialValues,
  } = props;

  const internalNewRequest = useSelector(state => state.forms.internalNewRequest);
  const request = useSelector(state => state.requests.details);
  const authUser = useSelector(state => state.auth.user);

  // eslint-disable-next-line no-unused-vars
  const [canEdit, setCanEdit] = useState(true);
  const [errorsList, setErrorsList] = useState({});

  useEffect(() => {
    const init = () => {
      if (Object.keys(internalNewRequest).length > 0) {
        const temp = setupLicenseInicialValues(internalNewRequest);
        setInitialValues(temp);
      } else {
        if (SERVICES_PROPERTIES[internalNewRequest.cdServico]) {
          setInitialValues(SERVICES_PROPERTIES[internalNewRequest.cdServico].formInitialValues);
        }
      }
    };
    init();
  }, [internalNewRequest, setInitialValues]);

  const validationSchema = useMemo(() => {
    return SERVICES_PROPERTIES[internalNewRequest.cdServico]
      ? SERVICES_PROPERTIES[internalNewRequest.cdServico].validationSchema
      : {};
  }, [internalNewRequest.cdServico]);

  const setupPayload = useCallback(
    (valuesCopy, requestJsonData, oldData) => {
      const payload = setupLicensePayload({ values: valuesCopy, requestJsonData, isInternalForm: false });
      let errors = {};
      if (internalNewRequest.cdServico === SERVICES_CODES.LICENCA_SEGUNDA_VIA) {
        errors = validateLicenseDuplicateRequest({ values: payload, oldData, intl, isInternalForm: true });
      } else {
        errors = validateLicenseRequest(
          {
            values: payload,
            cdServico: internalNewRequest.cdServico,
            intl,
            isInternalForm: true
          },
        );
      }
      if (Object.keys(errors).length > 0) {
        setErrorsList(errors);
        return;
      }
      return payload;
    },
    [internalNewRequest.cdServico, intl]
  );

  const handleSave = useCallback(
    (values, fromSubmit, oldData) => {
      onSave(values, fromSubmit, oldData, setupPayload);
    },
    [onSave, setupPayload],
  );

  const handleSubmit = useCallback(
    (values) => {
      onSubmit(values, onSave, setupPayload);
    },
    [onSave, onSubmit, setupPayload],
  );

  const contextProvider = useMemo(() => {
    return {
      isInternalForm: false,
      hideActionBtn: true,
      hideFinalizeBtn: false,
      mainColor: 'secondary',
      loadingDocuments,
      setLoadingDocuments,
      onCancel,
      onSave: handleSave,
      onSubmit: handleSubmit,
      initialValues,
      validationSchema,
      validateOnSave: true,
      canEdit,
      user: authUser,
      errorsList,
      setErrorsList,
    };
  }, [
    authUser,
    canEdit,
    errorsList,
    handleSave,
    handleSubmit,
    initialValues,
    loadingDocuments,
    onCancel,
    setLoadingDocuments,
    validationSchema,
  ]);

  const renderRequest = () => {
    const internalCdServico = internalNewRequest.cdServico || request.cdServico;
    let Component = SERVICES_PROPERTIES[internalCdServico] ? (
      SERVICES_PROPERTIES[internalCdServico].component
    ) : (
      <></>
    );

    return (
      <>
        {loading && (
          <div className={classes.centerProgress}>
            <CircularProgress size={20} />
          </div>
        )}
        {!loading && (
          <FormServiceContext.Provider value={contextProvider}>
            <Component {...props} />
          </FormServiceContext.Provider>
        )}
      </>
    );
  };

  return renderRequest();
};

export default injectIntl(Licenses);
